import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Compass, ListChecks, Sparkles, Timer, Target } from 'lucide-react';
import { CEFR_LEVELS, STUDY_MODE_LABELS, type LearningAdviceResult } from '@en3/shared';
import { ROUTES } from '@/app/routes';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * 学习建议面板：把"怎么学这一篇"渲染成可直接执行的计划。
 *
 * 为什么 `levelFit` 要放在最上面、而不是当一句副标题：
 * 用户打开这一页真正的问题是「这篇文章对我合不合适、值不值得花时间」。
 * 匹配度回答的就是这个问题，而 steps 回答的是"怎么做"。顺序反了，用户会先看到
 * 一堆步骤却不知道要不要照做。
 *
 * 另外两处诚实性设计：
 * - `generatedBy === 'rule'` 时标注「规则版建议」。规则版只是"按难度与生词分布套模板"，
 *   与真调了模型润色的版本质量差距很大，混在一起显示等于骗用户。
 * - `focusSentences` 用**全局句序**做锚点（`#s-{globalIndex}`），因为阅读页的句子
 *   id 就是 `s-{globalIndex}`。建议里的句子因此可以一键跳回原文那一句，
 *   而不是停在文章顶部让用户自己找。
 */
export function AdvicePanel({ result, className }: { result: LearningAdviceResult; className?: string }) {
  const { advice, levelFit, articleId, articleTitle, articleCefr, mode, generatedBy, cached, notes } = result;
  const isRule = generatedBy === 'rule';

  const totalMinutes = advice.steps.reduce((sum, step) => sum + (step.minutes ?? 0), 0);
  const readerBase = ROUTES.reader(articleId);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <Sparkles aria-hidden className="size-4 text-primary" />
          {STUDY_MODE_LABELS[mode]}建议
        </CardTitle>
        <div className="flex flex-wrap items-center gap-1.5">
          {articleCefr && (
            <Badge tone="primary" title="按文章词频与句长估算的 CEFR 档位">
              文章难度 {articleCefr}
            </Badge>
          )}
          <Badge tone={isRule ? 'warning' : 'primary'}>{isRule ? '规则版建议' : 'AI 润色'}</Badge>
          {cached && <Badge tone="muted">缓存</Badge>}
        </div>
      </CardHeader>

      <CardBody className="space-y-5 text-sm">
        <section>
          <p className="text-xs text-muted-foreground">
            文章：
            <Link to={readerBase} className="text-primary underline underline-offset-4">
              {articleTitle}
            </Link>
          </p>
          <p className="mt-2 flex gap-2 rounded-md border-l-2 border-primary bg-accent/40 px-3 py-2">
            <Target aria-hidden className="mt-0.5 size-4 shrink-0 text-primary" />
            <span>{levelFit}</span>
          </p>
        </section>

        <section>
          <h3 className="text-base font-semibold">{advice.title}</h3>
          <p className="mt-1 text-muted-foreground">{advice.purpose}</p>
          {advice.rationale && (
            <p className="mt-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              为什么这样建议：{advice.rationale}
            </p>
          )}
        </section>

        {/* 步骤：带序号 + 每步时长，并在末尾给总时长 —— 用户要据此决定"今天做还是留着周末做" */}
        <section>
          <h4 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            <ListChecks aria-hidden className="size-4" />
            执行步骤（{advice.steps.length} 步
            {totalMinutes > 0 ? ` · 预计共 ${totalMinutes} 分钟` : ''}）
          </h4>
          <ol className="mt-2 space-y-2.5">
            {advice.steps.map((step) => (
              <li key={`${step.order}-${step.title}`} className="flex gap-2.5">
                <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary tabular-nums">
                  {step.order}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    {step.title}
                    {typeof step.minutes === 'number' && step.minutes > 0 && (
                      <Badge tone="muted">
                        <Clock aria-hidden className="size-3" />
                        {step.minutes} 分钟
                      </Badge>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{step.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {advice.focusWords.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              重点词（{advice.focusWords.length}）
            </h4>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {advice.focusWords.map((word) => (
                <Link
                  key={word.surface}
                  to={readerBase}
                  title="到阅读页点这个词查义（会结合上下文给出文中义）"
                  className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs hover:bg-accent hover:text-accent-foreground"
                >
                  <span className="font-medium">{word.surface}</span>
                  {word.translation && <span className="text-muted-foreground">{word.translation}</span>}
                  {word.cefrLevel && (
                    <span
                      className="text-[10px] text-primary"
                      title={`CEFR ${word.cefrLevel}（${CEFR_LEVELS.indexOf(word.cefrLevel) + 1}/6 档）`}
                    >
                      {word.cefrLevel}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </section>
        )}

        {advice.focusSentences.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              重点句（{advice.focusSentences.length}）
            </h4>
            <ul className="mt-2 space-y-2">
              {advice.focusSentences.map((sentence) => (
                <li key={sentence.globalIndex}>
                  <Link
                    to={`${readerBase}#s-${sentence.globalIndex}`}
                    title="跳到原文这一句"
                    className="block rounded-md border px-3 py-2 transition-colors hover:border-primary hover:bg-accent/40"
                  >
                    <p className="text-sm leading-relaxed">{sentence.text}</p>
                    <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                      <Compass aria-hidden className="size-3 shrink-0" />
                      第 {sentence.globalIndex + 1} 句 · {sentence.note}
                      <ArrowRight aria-hidden className="ml-auto size-3" />
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-xs text-muted-foreground">
              点句子会跳到阅读页的对应位置；长难句可以在那里直接展开解析。
            </p>
          </section>
        )}

        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Timer aria-hidden className="size-3.5 shrink-0" />
          {totalMinutes > 0
            ? `按建议走一遍大约 ${totalMinutes} 分钟；时间不够时优先做前两步。`
            : '建议里没有给出时长，按自己的节奏完成即可。'}
        </p>

        {notes.length > 0 && (
          <p className="rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">{notes.join('；')}</p>
        )}
      </CardBody>
    </Card>
  );
}
